import type { CSSProperties } from 'react'

const MAX_GRADIENT_STOPS = 4

function normalizeHex(color: string): string | null {
  const t = color.trim()
  if (!t) return null
  if (/^#[0-9a-fA-F]{3}$/.test(t)) {
    const r = t[1]
    const g = t[2]
    const b = t[3]
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }
  if (/^#[0-9a-fA-F]{6}$/.test(t)) return t.toLowerCase()
  if (/^(rgb|hsl)a?\(/i.test(t)) return t
  return null
}

function withAlpha(hex: string, alphaHex: string): string {
  if (hex.startsWith('#') && hex.length === 7) return `${hex}${alphaHex}`
  return hex
}

const TRANSITION =
  'border-color 320ms ease, background-color 320ms ease, background 320ms ease, box-shadow 320ms ease'

/**
 * Subtle workspace-tinted chrome for chat user bubbles + composer.
 * One color: solid border/fill/glow. Multiple: soft gradient fill + multi-glow.
 */
export function chatWorkspaceAccentStyles(colors: Array<string | null | undefined>): {
  bubble: CSSProperties
  composer: CSSProperties
  composerFocused: CSSProperties
} | null {
  const unique = Array.from(
    new Set(
      colors
        .map((c) => (c ? normalizeHex(c) : null))
        .filter((c): c is string => !!c),
    ),
  ).slice(0, MAX_GRADIENT_STOPS)

  if (unique.length === 0) return null

  if (unique.length === 1) {
    const c = unique[0]
    const fill = withAlpha(c, '14')
    const border = withAlpha(c, '55')
    const glow = withAlpha(c, '40')
    return {
      bubble: {
        borderColor: border,
        backgroundColor: fill,
        boxShadow: `0 0 0 1px ${withAlpha(c, '18')}, 0 0 18px -10px ${glow}`,
        transition: TRANSITION,
      },
      composer: {
        borderColor: border,
        boxShadow: `0 0 0 1px ${withAlpha(c, '12')}, 0 0 22px -12px ${glow}`,
        transition: TRANSITION,
      },
      composerFocused: {
        borderColor: c,
        boxShadow: `0 0 0 1px ${withAlpha(c, '28')}, 0 0 28px -8px ${glow}`,
        transition: TRANSITION,
      },
    }
  }

  const fillGradient = `linear-gradient(135deg, ${unique.map((c) => withAlpha(c, '16')).join(', ')})`
  const glowParts = unique
    .map((c, i) => {
      const alpha = i === 0 ? '38' : i === unique.length - 1 ? '32' : '28'
      const spread = 10 + i * 4
      return `0 0 ${18 + i * 4}px -${spread}px ${withAlpha(c, alpha)}`
    })
    .join(', ')
  const border = withAlpha(unique[0], '66')
  const borderFocus = unique[0]

  return {
    bubble: {
      borderColor: border,
      backgroundImage: fillGradient,
      backgroundColor: withAlpha(unique[0], '10'),
      boxShadow: `0 0 0 1px ${withAlpha(unique[0], '14')}, ${glowParts}`,
      transition: TRANSITION,
    },
    composer: {
      borderColor: border,
      boxShadow: `0 0 0 1px ${withAlpha(unique[0], '10')}, ${glowParts}`,
      transition: TRANSITION,
    },
    composerFocused: {
      borderColor: borderFocus,
      boxShadow: `0 0 0 1px ${withAlpha(unique[0], '24')}, ${glowParts}`,
      transition: TRANSITION,
    },
  }
}

/** Resolve workspace colors for chat scope ids (order preserved, missing colors skipped). */
export function colorsForWorkspaceIds(
  workspaceIds: string[],
  workspaces: Array<{ id: string; color?: string | null }>,
): string[] {
  const byId = new Map(workspaces.map((w) => [w.id, w.color]))
  return workspaceIds
    .map((id) => byId.get(id))
    .filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
}
